import { css } from '@compiled/react';
import React, { useCallback } from 'react';
import { useLoaderData, useLocation, useNavigate } from 'react-router-dom';
import { CourseList, CourseStatus, ContentType } from '@atlassian/learning-components';
import { token } from '@atlaskit/tokens';

import { ContentfulLearningPath } from 'src/graphql/queries/GetLearningPath';
import { getCourseUrl, getLessonUrl, withPrefix } from 'src/utils/getUrl';
import { getLearningPathsHubPart } from 'src/utils/pathUtils';
import { getSlug } from 'src/utils/slug';
import { LearningPathLoaderProps } from 'src/pages/LearningPathPage/loader';
import { useCourseProgress, useLessonProgress } from 'src/store/progress/progressStore';
import { LearningProgressStatus } from 'src/services/http/progress';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { sendTrackEvent } from 'src/utils/analytics';
import { getCourseData, getLessonsData } from 'src/pages/LearningPathPage/utils';
import { FIELD_ID } from 'src/pages/CoursePage/utils';
import { getNextItemSlug } from 'src/pages/utils/navigation';

export const CourseListWrapper: React.FC = () => {
  const { learningPath } = useLoaderData() as LearningPathLoaderProps;
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading } = useAtlassianUser();
  const [{ progressForCourses, isLoadingProgressForCourses }] = useCourseProgress();
  const [{ progressForLessons, isLoadingProgressForLessons }] = useLessonProgress();

  const learningPathSlug = getSlug(learningPath);

  const onCourseTitleClick = useCallback(
    (course: ContentfulLearningPath['coursesCollection']['items'][0]): void => {
      const hasNotStartedLearningPath =
        !progressForCourses?.overallStatus || progressForCourses.overallStatus === LearningProgressStatus.NOT_STARTED;

      if (hasNotStartedLearningPath) {
        sendTrackEvent(
          'learningApp',
          'learningPath',
          'started',
          {
            learningPathSlug,
          },
          user?.account_id,
        );
      }

      if (course && learningPath) {
        navigate(`/path/${learningPath.slug}/course/${course.slug}`);
      }
    },
    [navigate, learningPath, progressForCourses, user, learningPathSlug],
  );

  const handleHrefLink = useCallback(
    (course: ContentfulLearningPath['coursesCollection']['items'][0]): string => {
      if (!course || !learningPath) {
        return '';
      }

      const baseUrl = getCourseUrl(getSlug(course), learningPath?.slug);
      const hubPart = getLearningPathsHubPart(location.pathname);
      const url = hubPart ? `${hubPart}${baseUrl}` : baseUrl;

      return withPrefix(url);
    },
    [learningPath, location.pathname],
  );

  const getCourseStatus = (courseId: string): CourseStatus => {
    if (isLoading || isLoadingProgressForCourses) {
      return CourseStatus.DEFAULT;
    }

    if (!progressForCourses || !user) {
      return CourseStatus.DEFAULT;
    }

    const courseProgress = progressForCourses.courses.find((p) => p.courseId === courseId);

    if (courseProgress?.status === LearningProgressStatus.IN_PROGRESS) {
      return CourseStatus.IN_PROGRESS;
    }

    if (courseProgress?.status === LearningProgressStatus.COMPLETED) {
      return CourseStatus.FINISHED;
    }

    return CourseStatus.DEFAULT;
  };

  const getLessonCompletionStatus = (lessonId: string): boolean => {
    if (isLoading || isLoadingProgressForLessons || isLoadingProgressForCourses || !progressForLessons || !user) {
      return false;
    }

    const lessonProgress = progressForLessons.lessons.find((p) => p.lessonId === lessonId);

    return lessonProgress?.status === LearningProgressStatus.COMPLETED;
  };

  const handleCourseButtonClick = useCallback(
    (course: ContentfulLearningPath['coursesCollection']['items'][0]): void => {
      const courseSlug = getSlug(course);

      const navigateToLesson = (lessonSlug: string): void => {
        navigate(
          withPrefix(
            getLessonUrl({
              lessonSlug,
              courseSlug: courseSlug,
            }),
            location.pathname.replace(/\/$/, ''),
          ),
        );
      };

      const lessons = course?.lessonsCollection?.items;

      if (!lessons?.length) {
        return;
      }

      const nextLessonSlug = getNextItemSlug({
        items: lessons,
        progress: {
          overallStatus: progressForLessons?.overallStatus || LearningProgressStatus.NOT_STARTED,
          lessons: progressForLessons?.lessons || [],
        },
        itemIdField: FIELD_ID,
        contentType: ContentType.COURSE,
      });

      navigateToLesson(nextLessonSlug);
    },
    [navigate, location, progressForLessons],
  );

  return (
    <div css={courseListContainerStyles} data-testid="course-list-wrapper">
      {learningPath.coursesCollection.items.map((course) => {
        const courseData = getCourseData(course, getCourseStatus, handleHrefLink);
        const lessons = getLessonsData(course, getLessonCompletionStatus);

        return (
          <CourseList
            key={course.sys.id}
            course={courseData}
            lessons={lessons}
            onLessonClick={(slug) => {
              navigate(`/path/${learningPath.slug}/course/${course.slug}/lesson/${slug}`);
            }}
            onCourseTitleClick={() => {
              onCourseTitleClick(course);
            }}
            onCourseButtonClick={() => {
              handleCourseButtonClick(course);
            }}
          />
        );
      })}
    </div>
  );
};

const courseListContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.200'),
  alignSelf: 'stretch',
  width: '100%',
});
